import moment from 'moment';

const helpers = {
  /**
   * Getting last week range monday-sunday
   */
  getLastWeekRange() {
    const monday = moment().startOf('isoWeek').format('YYYY-MM-DD');
    const isoMonday = moment(monday).utcOffset(0, true).toISOString();

    const sunday = moment().endOf('isoWeek').format('YYYY-MM-DD');
    const isoSunday = moment(sunday).utcOffset(0, true).toISOString();

    return {
      weekFirstDay: isoMonday,
      weekLastDay: isoSunday
    };
  },
  getCssVariable(variable) {
    // eslint-disable-next-line quotes
    const light = "[data-theme='light']";
    // eslint-disable-next-line quotes
    const dark = "[data-theme='dark']";

    const el = document.querySelector(light) || document.querySelector(dark);
    const style = getComputedStyle(el);

    return style.getPropertyValue(variable);
  },

  /**
   * Sort items from the smallest to the largest, by the key passed in the second argument
   * @param {Object} - object containing two properties
   * 1. arr - an array of objects
   * 2. key -  the key by which the array should be sorted (the values must be numbers)
   * @returns {Arr} - returns an array of objects sorted based on "key" value
   */
  sortArrayItemsByKey(params) {
    const { arr, key, reverse = false } = params;
    return arr.sort((a, b) => {
      if (reverse) {
        return b[key] - a[key];
      }
      return a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0;
    });
  },

  /**
   * Video generation time formatting utilities
   * Used by both VideoGenerator and GlobalVideoGenerationProgress components
   */

  // Calculate time remaining using the same logic across all components
  calculateTimeRemaining(generation) {
    // Use progressStartTime if available (synced from VideoGenerator), otherwise fall back to startTime
    const startTime = generation.progressStartTime || new Date(generation.startTime).getTime();

    if (!startTime || generation.progress >= 100 || generation.status === 'error') {
      return null;
    }

    const elapsed = Date.now() - startTime;
    const progressDuration = 240000; // 4 minutes in milliseconds
    const progressDecimal = generation.progress / 100;

    // Avoid division by zero and handle very small progress values
    if (progressDecimal <= 0.01) {
      // For very early progress, show the full duration minus elapsed time
      const remaining = Math.max(0, progressDuration - elapsed);
      return remaining;
    }

    // For progress near completion (95%+), show a small remaining time
    if (progressDecimal >= 0.95) {
      const remaining = Math.min(30000, Math.max(5000, progressDuration - elapsed)); // 5-30 seconds
      return remaining;
    }

    // Calculate estimated total time based on current progress
    const estimatedTotalTime = elapsed / progressDecimal;
    const remainingTime = estimatedTotalTime - elapsed;

    // Handle edge cases and apply reasonable bounds
    if (remainingTime <= 0) {
      return 5000; // Show at least 5 seconds remaining
    }

    // Cap the maximum remaining time to avoid unrealistic estimates
    const maxRemainingTime = progressDuration - elapsed + 30000; // Add 30s buffer
    const boundedRemainingTime = Math.min(remainingTime, maxRemainingTime);

    return boundedRemainingTime;
  },

  // Format time remaining in user-friendly format
  formatTimeRemaining(remainingMs) {
    if (remainingMs === null || remainingMs <= 0) {
      return null;
    }

    const totalSeconds = Math.ceil(remainingMs / 1000);

    // Show "Almost done..." for very short remaining times
    if (totalSeconds <= 10) {
      return 'Almost done...';
    }

    if (totalSeconds < 60) {
      return `${totalSeconds}s remaining`;
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes < 60) {
      return seconds > 0 ? `${minutes}m ${seconds}s remaining` : `${minutes}m remaining`;
    }

    // For times over an hour (shouldn't happen with 4min duration, but just in case)
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m remaining`;
  },

  // Main function that calculates and formats time remaining
  getFormattedTimeRemaining(generation) {
    const remainingMs = this.calculateTimeRemaining(generation);
    return this.formatTimeRemaining(remainingMs);
  }
};

export default helpers;
